import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { mortgageMiniCalculatorApi } from '../api';
import { closeModal, openUnfinishedAnketaModal, setUnfinishedAnketaModalNewAnketaUrl } from '../redux';
import { selectIsCianAuthorized, selectIsOffersFetching, selectSaveOffersParams } from '../selectors/calculator';
import { hasUnfinishedAnketa, redirect, withRequestTimeout } from '../utils';
import { IOfferForAnalytic } from '../utils/tracking/types';
import { useBuildShortFormUrl } from './useBuildShortFormUrl';

export interface IUseShortFormAuthActionOptions {
  trackingFunction?: () => void;
  offerForAnalytic?: IOfferForAnalytic | null;
  bankId?: number | null;
}

export interface IUseShortFormAuthActionReturn {
  isLoading: boolean;
  handleShortFormAuthAction: () => Promise<void>;
}

export const useShortFormAuthAction = (options: IUseShortFormAuthActionOptions = {}): IUseShortFormAuthActionReturn => {
  const { trackingFunction, offerForAnalytic, bankId } = options;
  const buildShortFormUrl = useBuildShortFormUrl();
  const dispatch = useDispatch();
  const isCianAuthorized = useSelector(selectIsCianAuthorized);
  const saveOffersParams = useSelector(selectSaveOffersParams);
  const isOffersFetching = useSelector(selectIsOffersFetching);

  /* Ответы приходят не сразу, за это время человек успевает закрыть модалку оффера и уйти
     листать выдачу: показывать ему модалку черновика задним числом нельзя. Редирект наоборот
     доводим до конца — переход в анкету он и просил кликом, а другого пути к ней у него нет */
  const isMounted = useRef(true);

  useEffect(
    () => () => {
      isMounted.current = false;
    },
    [],
  );
  const [saveOffersFromListing, { isLoading: isSaving }] =
    mortgageMiniCalculatorApi.endpoints.saveOffersFromListing.useMutation();
  const [fetchActualApplication, { isFetching: isActualApplicationFetching }] =
    mortgageMiniCalculatorApi.endpoints.getActualApplication.useLazyQuery();

  /* Выдачу сохраняем только ради applicationId: по нему анкета достанет параметры расчёта.
     programsVersion тут всегда есть — без него useAuthAction не пускает в эту ветку */
  const saveOffersForAnketa = async (): Promise<number | null> => {
    try {
      const { applicationId } = await withRequestTimeout(saveOffersFromListing(saveOffersParams));

      return applicationId;
    } catch {
      return null;
    }
  };

  const buildNewAnketaUrl = (applicationId: number | null) =>
    buildShortFormUrl({ draftAction: 'new', applicationId, bankId });

  const handleShortFormAuthAction = async () => {
    /* Трекинг до запросов: после await уход со страницы потеряет клик только в группе B */
    trackingFunction?.();

    if (!isCianAuthorized) {
      redirect(buildNewAnketaUrl(await saveOffersForAnketa()));

      return;
    }

    // Сохранение не ждём: показывать модалку или нет, решает только наличие черновика
    const savingApplicationId = saveOffersForAnketa();
    const hasAnketa = await hasUnfinishedAnketa(fetchActualApplication());

    if (!hasAnketa) {
      redirect(buildNewAnketaUrl(await savingApplicationId));

      return;
    }

    if (!isMounted.current) {
      return;
    }

    /* Модалка оффера, из которой мог прийти клик, лежит в общем ModalWindow — иначе модалка в модалке */
    dispatch(closeModal());
    dispatch(
      openUnfinishedAnketaModal({
        offerForAnalytic: offerForAnalytic ?? null,
        newAnketaUrl: buildNewAnketaUrl(null),
      }),
    );

    const savedApplicationId = await savingApplicationId;

    /* Без isMounted: `closeModal` выше размонтирует модалку оффера вместе с этим хуком, а модалку
       черновика рисует корневой контейнер. Закрытую ссылку не воскресит сам редьюсер. */
    if (savedApplicationId) {
      dispatch(setUnfinishedAnketaModalNewAnketaUrl(buildNewAnketaUrl(savedApplicationId)));
    }
  };

  return { isLoading: isSaving || isActualApplicationFetching || isOffersFetching, handleShortFormAuthAction };
};
